import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import MercadoPagoBricks from '../../components/MercadoPagoBricks';
import './CartPage.css';
import { log, logError, warn } from '../../utils/logger';
import { formatARS } from '../../utils/currency';
import {
  checkCartLinesStock,
  isPhysicalCartLine,
  matchStockLine,
  notifyStockWarning,
} from '../../utils/cartStock.js';
import { useStoreSettings } from '../../hooks/useStoreSettings';
import ShippingNotice from '../../components/ShippingNotice/ShippingNotice';
import { orderLinesFromCart } from '../../utils/whatsapp';

/** Mismo id que en servidor / AdminDashboard — solo para logs de test de pago. */
const ADMIN_TEST_PAYMENT_CART_ID = 'admin-test-payment-custom';

function maskCardTokenForLog(t) {
  if (!t || typeof t !== 'string') return null;
  if (t.length <= 12) return '[redacted]';
  return `${t.slice(0, 6)}…${t.slice(-4)} (len=${t.length})`;
}

function logAdminTestPaymentDebug(label, payload) {
  console.group(`[Always Aligners · Test de pago admin] ${label}`);
  console.log(payload);
  console.groupEnd();
}

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return null;
  const clean = String(imagePath).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(clean)) return clean;
  const base = getApiBaseUrl();
  if (clean.startsWith('/')) return `${base}${clean}`;
  if (clean.startsWith('uploads/')) return `${base}/${clean}`;
  return `${base}/uploads/${clean}`;
};

function CartPaymentModal({
  show,
  onHide,
  cart,
  processingPayment,
  error,
  onPaymentSuccess,
  onPaymentError,
  onMpBricksSubmitReady,
  mpBrickPayReady,
  onModalPayClick,
  subtotal,
}) {
  const total = subtotal;
  const hasPhysical = cart.some(isPhysicalCartLine);
  const { settings } = useStoreSettings();

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="CART-modal-checkout-dialog"
      contentClassName="CART-modal-checkout-content"
    >
      <Modal.Header closeButton className="CART-modal-checkout-header">
        <Modal.Title className="CART-modal-checkout-title">
          <span className="CART-modal-checkout-title-icon" aria-hidden="true">
            <i className="fas fa-credit-card" />
          </span>
          Completar pago
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="CART-modal-checkout-body">
        {error && (
          <div className="alert alert-danger CART-modal-checkout-alert" role="alert">
            <i className="fas fa-exclamation-circle me-2" aria-hidden="true" />
            {error}
          </div>
        )}

        {hasPhysical && (
          <ShippingNotice
            variant="checkout"
            freeShippingThreshold={settings.freeShippingThreshold}
          />
        )}

        <Row className="g-2 g-lg-3 CART-modal-checkout-row align-items-stretch">
          <Col xs={12} className="CART-modal-col CART-modal-col--pago d-flex">
            <div className="CDF-wrap CDF-wrap--mercadopago w-100 mb-0">
              <header className="CDF-head">
                <div className="CDF-head-icon" aria-hidden="true">
                  <i className="fas fa-credit-card" />
                </div>
                <div className="CDF-head-text">
                  <h2 className="CDF-title">Medio de pago</h2>
                </div>
              </header>
              <div className="CDF-body CDF-body--mp">
                <MercadoPagoBricks
                  embedded
                  externalSubmitButton
                  onExternalSubmitReady={onMpBricksSubmitReady}
                  amount={total}
                  items={cart.map((item) => ({
                    id: item.id,
                    title: item.variantName ? `${item.name} — ${item.variantName}` : item.name,
                    quantity: item.quantity,
                    unit_price: item.price,
                    productType: item.productType,
                    itemVariantId: item.itemVariantId ?? undefined,
                  }))}
                  onSuccess={(formData) => onPaymentSuccess(formData)}
                  onError={onPaymentError}
                  onReady={() => {
                    log('✅ Formulario de pago listo en modal');
                  }}
                  disabled={processingPayment}
                />
              </div>
            </div>
          </Col>
        </Row>

        <div className="CART-modal-summary">
          <h5>Resumen de compra</h5>
          <div className="CART-modal-summary-row">
            <span>Productos</span>
            <strong>{formatARS(subtotal)}</strong>
          </div>
          <div className="CART-modal-summary-divider" />
          <div className="CART-modal-summary-total">
            <strong>Total</strong>
            <strong className="CART-modal-summary-total-amount">{formatARS(total)}</strong>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="CART-modal-checkout-footer">
        <Button variant="secondary" onClick={onHide} disabled={processingPayment}>
          <i className="fas fa-times me-2" aria-hidden="true" />
          Cancelar
        </Button>
        <Button
          type="button"
          variant="primary"
          className="CART-modal-checkout-pay"
          onClick={onModalPayClick}
          disabled={processingPayment || !mpBrickPayReady}
        >
          {processingPayment ? (
            <>
              <i className="fas fa-spinner fa-spin me-2" aria-hidden="true" />
              Procesando…
            </>
          ) : (
            <>
              <i className="fas fa-lock me-2" aria-hidden="true" />
              Pagar {formatARS(total)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const CartPage = () => {
  const { cart, removeFromCart, clearCart, updateQuantity } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const mpBrickPayRef = useRef(null);
  const [mpBrickPayReady, setMpBrickPayReady] = useState(false);
  const [stockLimits, setStockLimits] = useState({});

  const cartHasAdminTest = useMemo(
    () =>
      cart.some(
        (item) =>
          item.productType === 'custom' && String(item.id) === ADMIN_TEST_PAYMENT_CART_ID,
      ),
    [cart],
  );

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const stockKey = (item) =>
    `${item.productType}:${item.id}:${item.itemVariantId ?? 'base'}`;

  useEffect(() => {
    if (!cart.some(isPhysicalCartLine)) {
      setStockLimits({});
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const { lines } = await checkCartLinesStock(cart);
        if (cancelled) return;

        const limits = {};
        for (const item of cart) {
          if (!isPhysicalCartLine(item)) continue;
          const stockLine = matchStockLine(
            lines,
            item.id,
            item.productType,
            item.itemVariantId,
          );
          if (stockLine) {
            limits[stockKey(item)] = stockLine.maxQuantity;
            if (!stockLine.ok && stockLine.maxQuantity > 0 && item.quantity > stockLine.maxQuantity) {
              updateQuantity(
                item.id,
                item.productType,
                stockLine.maxQuantity,
                item.itemVariantId,
              );
            } else if (stockLine.maxQuantity <= 0) {
              removeFromCart(item.id, item.productType, item.itemVariantId);
              notifyStockWarning(stockLine.message || `${item.name} ya no tiene stock.`);
            }
          }
        }
        setStockLimits(limits);
      } catch (err) {
        logError('[CartPage] Error sincronizando stock:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cart, removeFromCart, updateQuantity]);

  const handleQuantityChange = useCallback(
    async (itemId, productType, newQuantity, itemVariantId = null) => {
      if (newQuantity < 1) {
        removeFromCart(itemId, productType, itemVariantId);
        return;
      }
      await updateQuantity(itemId, productType, newQuantity, itemVariantId);
    },
    [updateQuantity, removeFromCart],
  );

  const handleRemoveItem = useCallback(
    (itemId, productType, itemVariantId = null) => {
      if (window.confirm('¿Estás seguro de que deseas eliminar este producto del carrito?')) {
        removeFromCart(itemId, productType, itemVariantId);
      }
    },
    [removeFromCart],
  );

  const handleCheckout = useCallback(async () => {
    if (!user || !token) {
      navigate('/login', { state: { from: '/cart' } });
      return;
    }

    if (cart.length === 0) {
      setError('El carrito está vacío');
      return;
    }

    if (cart.some(isPhysicalCartLine)) {
      try {
        const { valid, lines } = await checkCartLinesStock(cart);
        if (!valid) {
          const firstBad = lines.find((line) => !line.ok);
          setError(firstBad?.message || 'Hay productos sin stock suficiente en el carrito.');
          notifyStockWarning(firstBad?.message || 'Revisá las cantidades del carrito.');
          return;
        }
      } catch (err) {
        setError('No se pudo verificar el stock. Intentá de nuevo.');
        return;
      }
    }

    setShowPaymentModal(true);
    setError(null);
  }, [user, token, cart, navigate]);

  const handleClosePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    setError(null);
    setMpBrickPayReady(false);
    mpBrickPayRef.current = null;
  }, []);

  const handlePaymentSuccess = useCallback(
    async (formData) => {
      setProcessingPayment(true);
      setError(null);

      const payTotal = Math.round(subtotal * 100) / 100;

      try {
        if (cart.some(isPhysicalCartLine)) {
          const { valid, lines } = await checkCartLinesStock(cart);
          if (!valid) {
            const firstBad = lines.find((line) => !line.ok);
            const msg = firstBad?.message || 'Stock insuficiente. Actualizá el carrito e intentá de nuevo.';
            setError(msg);
            notifyStockWarning(msg);
            setProcessingPayment(false);
            return;
          }
        }

        if (!formData?.token) {
          setError('Completá todos los datos de la tarjeta para continuar.');
          setProcessingPayment(false);
          return;
        }

        log('💳 Procesando pago con Bricks...', formData);

        const requestBody = {
          token: formData.token,
          issuer_id: formData.issuer_id,
          payment_method_id: formData.payment_method_id,
          installments: formData.installments,
          items: cart.map((item) => ({
            id: item.id,
            title: item.variantName ? `${item.name} — ${item.variantName}` : item.name,
            quantity: item.quantity,
            unit_price: item.price,
            productType: item.productType,
            itemVariantId: item.itemVariantId ?? undefined,
          })),
          total: payTotal,
        };

        if (cartHasAdminTest) {
          logAdminTestPaymentDebug('Petición a /api/payments/process-bricks', {
            apiUrl: buildApiUrl('/api/payments/process-bricks'),
            total: payTotal,
            items: requestBody.items,
            bricks: {
              ...requestBody,
              token: maskCardTokenForLog(requestBody.token),
            },
          });
        }

        const response = await fetch(buildApiUrl('/api/payments/process-bricks'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
          if (cartHasAdminTest) {
            logAdminTestPaymentDebug(`Error HTTP ${response.status} (cuerpo de respuesta)`, errorData);
          }
          const msg = [errorData.error, errorData.details].filter(Boolean).join(' — ');
          throw new Error(msg || `Error al procesar el pago (${response.status})`);
        }

        const data = await response.json();
        log('📦 Respuesta del servidor:', data);
        if (cartHasAdminTest) {
          logAdminTestPaymentDebug('Respuesta OK (JSON completo)', data);
        }

        if (data.success === true && data.status === 'approved') {
          log('✅ Pago aprobado, limpiando carrito y redirigiendo...');
          const physicalLines = orderLinesFromCart(cart.filter(isPhysicalCartLine));
          const hasPhysical = physicalLines.length > 0;
          clearCart();
          log('✅ Carrito limpiado después del pago exitoso');

          setTimeout(() => {
            navigate('/payment-success', {
              state: {
                payment_id: data.payment_id,
                transaction_id: data.transaction_id,
                order_id: data.order_id,
                order_number: data.order_number,
                status: data.status || 'approved',
                fromBricksPayment: true,
                hasPhysical,
                physicalLines,
                total: payTotal,
              },
            });
          }, 100);
        } else {
          warn('⚠️ Pago no aprobado:', data);
          setError(data.message || `El pago está ${data.status}. ${data.status_detail || ''}`);
          setProcessingPayment(false);
        }
      } catch (err) {
        logError('[CartPage] Error procesando pago:', err);
        if (cartHasAdminTest) {
          logAdminTestPaymentDebug('Excepción al procesar pago (catch)', {
            message: err?.message,
            stack: err?.stack,
          });
        }
        setError(err.message || 'Error al procesar el pago. Por favor, intenta nuevamente.');
        setProcessingPayment(false);
      }
    },
    [token, cart, subtotal, navigate, clearCart, cartHasAdminTest],
  );

  const handlePaymentError = useCallback(
    (paymentErr) => {
      logError('[CartPage] Error en Bricks:', paymentErr);
      if (cartHasAdminTest) {
        logAdminTestPaymentDebug('Error en Mercado Pago Bricks (callback onError)', {
          message: paymentErr?.message,
          type: paymentErr?.type,
          cause: paymentErr?.cause,
        });
      }
      setError(paymentErr?.message || 'Error al procesar el pago. Por favor, intenta nuevamente.');
      setProcessingPayment(false);
    },
    [cartHasAdminTest],
  );

  const handleMpBricksSubmitReady = useCallback((api) => {
    mpBrickPayRef.current = api;
    setMpBrickPayReady(!!api?.submit);
  }, []);

  const getProductTypeLabel = (type) => {
    const labels = {
      course: 'Curso',
      event: 'Evento',
      item: 'Item',
      items: 'Item',
      physical: 'Item',
      consult: 'Asesoría',
      custom: 'Custom (test)',
    };
    return labels[type] || 'Producto';
  };

  if (cart.length === 0) {
    return (
      <div className="CART-page">
        <div className="CART-container">
          <div className="CART-empty">
            <div className="CART-empty-icon">
              <i className="fas fa-shopping-cart" />
            </div>
            <h2 className="CART-empty-title">Tu carrito está vacío</h2>
            <p className="CART-empty-text">
              Agrega productos al carrito para comenzar a comprar.
            </p>
            <button
              className="CART-btn CART-btn-primary"
              onClick={() => navigate('/courses')}
            >
              <i className="fas fa-arrow-left" />
              Explorar Cursos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CartPageContent
      cart={cart}
      error={error}
      setError={setError}
      processingPayment={processingPayment}
      showPaymentModal={showPaymentModal}
      subtotal={subtotal}
      handleQuantityChange={handleQuantityChange}
      handleRemoveItem={handleRemoveItem}
      handleCheckout={handleCheckout}
      handleClosePaymentModal={handleClosePaymentModal}
      handlePaymentSuccess={handlePaymentSuccess}
      handlePaymentError={handlePaymentError}
      handleMpBricksSubmitReady={handleMpBricksSubmitReady}
      mpBrickPayReady={mpBrickPayReady}
      mpBrickPayRef={mpBrickPayRef}
      stockLimits={stockLimits}
      stockKey={stockKey}
      getProductTypeLabel={getProductTypeLabel}
      navigate={navigate}
      clearCart={clearCart}
    />
  );
};

function CartPageContent({
  cart,
  error,
  setError,
  processingPayment,
  showPaymentModal,
  subtotal,
  handleQuantityChange,
  handleRemoveItem,
  handleCheckout,
  handleClosePaymentModal,
  handlePaymentSuccess,
  handlePaymentError,
  handleMpBricksSubmitReady,
  mpBrickPayReady,
  mpBrickPayRef,
  getProductTypeLabel,
  navigate,
  clearCart,
  stockLimits,
  stockKey,
}) {
  const { settings } = useStoreSettings();
  const hasPhysical = cart.some(isPhysicalCartLine);
  const handleModalPayClick = useCallback(async () => {
    const api = mpBrickPayRef.current;
    if (!api?.submit || processingPayment) return;

    try {
      const formData = await api.submit();
      if (!formData?.token) {
        setError('Completá todos los datos de la tarjeta para continuar.');
        return;
      }
      await handlePaymentSuccess(formData);
    } catch (err) {
      if (err?.cause === 'incomplete_fields' || err?.type === 'non_critical') {
        setError(err?.message || 'Completá todos los datos de la tarjeta para continuar.');
        return;
      }
      const fallback =
        err?.message ||
        (err?.cause && (typeof err.cause === 'string' ? err.cause : err.cause?.message)) ||
        'Revisá los datos de la tarjeta e intentá de nuevo.';
      handlePaymentError({ message: fallback });
    }
  }, [
    processingPayment,
    handlePaymentSuccess,
    handlePaymentError,
    mpBrickPayRef,
    setError,
  ]);

  return (
    <div className="CART-page">
      <div className="CART-container">
        <h1 className="CART-title">
          <i className="fas fa-shopping-cart" />
          Carrito de Compras
        </h1>

        {hasPhysical && (
          <ShippingNotice
            variant="checkout"
            freeShippingThreshold={settings.freeShippingThreshold}
          />
        )}

        {error && (
          <div className="CART-error">
            <i className="fas fa-exclamation-circle" />
            <span>{error}</span>
            <button
              className="CART-error-close"
              onClick={() => setError(null)}
              aria-label="Cerrar error"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )}

        <div className="CART-content">
          <div className="CART-items-section">
            <div className="CART-items-header">
              <h2 className="CART-items-title">
                <i className="fas fa-shopping-bag" />
                Productos ({cart.length})
              </h2>
            </div>
            <div className="CART-items-list">
              {cart.map((item, index) => {
                const imageUrl = resolveImageUrl(item.image);
                const vKey = item.itemVariantId != null ? String(item.itemVariantId) : 'base';
                const maxStock = stockLimits?.[stockKey(item)];
                return (
                  <div
                    key={`${item.id}-${item.productType}-${vKey}-${index}`}
                    className="CART-item"
                  >
                    <div className="CART-item-image">
                      {imageUrl ? (
                        <img src={imageUrl} alt={item.name} />
                      ) : (
                        <div className="CART-item-placeholder">
                          <i className="fas fa-image" />
                        </div>
                      )}
                    </div>
                    <div className="CART-item-info">
                      <h3 className="CART-item-name">{item.name}</h3>
                      {item.variantName ? (
                        <span className="CART-item-variant">{item.variantName}</span>
                      ) : null}
                      <span className="CART-item-type">
                        {getProductTypeLabel(item.productType)}
                      </span>
                      <div className="CART-item-price-unit">
                        {formatARS(item.price)} c/u
                      </div>
                    </div>
                    <div className="CART-item-quantity">
                      <label className="CART-quantity-label">Cantidad</label>
                      <input
                        type="number"
                        min="1"
                        max={maxStock > 0 ? maxStock : undefined}
                        value={item.quantity}
                        onChange={(e) =>
                          handleQuantityChange(
                            item.id,
                            item.productType,
                            parseInt(e.target.value, 10) || 1,
                            item.itemVariantId,
                          )
                        }
                        className="CART-quantity-input"
                      />
                      {maxStock > 0 && (
                        <span className="CART-quantity-stock text-muted small">
                          Máx. {maxStock}
                        </span>
                      )}
                    </div>
                    <div className="CART-item-remove">
                      <button
                        className="CART-btn-remove"
                        onClick={() =>
                          handleRemoveItem(item.id, item.productType, item.itemVariantId)
                        }
                        title="Eliminar producto"
                        aria-label="Eliminar producto"
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="CART-summary-section">
            <div className="CART-summary-card">
              <div className="CART-summary-header">
                <h2 className="CART-summary-title">
                  <i className="fas fa-receipt" />
                  Resumen
                </h2>
              </div>
              <div className="CART-summary-body">
                <div className="CART-summary-row">
                  <span className="CART-summary-label">Subtotal</span>
                  <span className="CART-summary-value">{formatARS(subtotal)}</span>
                </div>
                <div className="CART-summary-divider" />
                <div className="CART-summary-total">
                  <span className="CART-total-label">Total estimado</span>
                  <span className="CART-total-amount">{formatARS(subtotal)}</span>
                </div>
                <button
                  className="CART-btn CART-btn-checkout"
                  onClick={handleCheckout}
                  disabled={processingPayment || cart.length === 0}
                >
                  <i className="fas fa-credit-card" />
                  Proceder al Pago
                </button>
                <button
                  className="CART-btn CART-btn-secondary"
                  onClick={() => navigate('/courses')}
                >
                  <i className="fas fa-arrow-left" />
                  Seguir Comprando
                </button>
                <button
                  className="CART-btn-link"
                  onClick={() => {
                    if (window.confirm('¿Estás seguro de que deseas vaciar el carrito?')) {
                      clearCart();
                    }
                  }}
                >
                  <i className="fas fa-trash-alt" />
                  Vaciar Carrito
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CartPaymentModal
        show={showPaymentModal}
        onHide={handleClosePaymentModal}
        cart={cart}
        subtotal={subtotal}
        processingPayment={processingPayment}
        error={error}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        onMpBricksSubmitReady={handleMpBricksSubmitReady}
        mpBrickPayReady={mpBrickPayReady}
        onModalPayClick={handleModalPayClick}
      />
    </div>
  );
}

export default CartPage;
